import type {
  PathEntry,
  PathEntryValueType,
} from "../layout/data-source-schema.js";
import type {
  DataSourceId,
  InjectedData,
  OrganisationData,
} from "../layout/types.js";
import {
  resolveCompetitionOrganisation,
  toOrganisationData,
} from "../layout/adapter.js";
import {
  CUSTOM_DISCIPLINE_ATHLETE_ID_KEY,
  CUSTOM_DISCIPLINE_ATHLETE_ORDER_KEY,
  type CustomDisciplineAthleteRecord,
} from "./records.js";
import {
  CUSTOM_DISCIPLINE_ATHLETE_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE,
  CUSTOM_DISCIPLINE_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_GROUPS_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE,
} from "./sources.js";

export const CUSTOM_DISCIPLINE_CELL_TEXT_BINDING_SOURCES = [
  CUSTOM_DISCIPLINE_ATHLETE_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE,
] as const satisfies readonly DataSourceId[];

export type CustomDisciplinePathSchemaMap = Record<string, PathEntry[] | null>;

/** Resolves the meet organiser from its id and the public organisation catalog. */
export function resolveCustomDisciplineOrganisation(input: {
  currentMeet?: unknown;
  organisation?: OrganisationData | null;
  organisations?: readonly unknown[];
}): OrganisationData | null {
  if (input.organisation !== undefined) return input.organisation;
  const meet = input.currentMeet as
    | {
        organisationId?: string | null;
        organisation?: OrganisationData | null;
      }
    | null
    | undefined;
  const matching = input.organisations?.find(
    (organisation) =>
      (organisation as OrganisationData | null)?.id === meet?.organisationId,
  );
  return resolveCompetitionOrganisation({
    meetOrganisationId: meet?.organisationId,
    federationOrganisation:
      toOrganisationData(matching as OrganisationData | undefined) ??
      toOrganisationData(meet?.organisation),
  });
}

export type BuildCustomDisciplineCellRuntimeDataInput = {
  athlete?: CustomDisciplineAthleteRecord;
  athletes: CustomDisciplineAthleteRecord[];
  currentValue: string;
  selectedRowId?: string;
  selectedColumnKey?: string;
  instanceKey: string;
};

function mockScalarForValueType(
  valueType?: PathEntryValueType,
): string | number | boolean {
  switch (valueType) {
    case "number":
      return 42;
    case "boolean":
      return true;
    case "date":
      return "2026-01-15";
    default:
      return "Exemple";
  }
}

/**
 * Builds preview/mock injected data for the custom-discipline cell layout editor.
 *
 * @param schema - Athlete path overlay from {@link buildCustomDisciplineObjectPathSchema}.
 * @param options - Optional overrides such as the preview `currentValue` label.
 * @returns Partial {@link InjectedData} merged into the Display Editor mock panel.
 */
export function buildCustomDisciplineCellMockData(
  schema?: CustomDisciplinePathSchemaMap | null,
  options?: { currentValue?: string },
): Partial<InjectedData> {
  const athleteSchema = schema?.[CUSTOM_DISCIPLINE_ATHLETE_SOURCE] ?? [];
  const groupSchema =
    schema?.[CUSTOM_DISCIPLINE_GROUP_SOURCE] ??
    schema?.[CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE] ??
    schema?.[CUSTOM_DISCIPLINE_GROUPS_SOURCE] ??
    [];
  const athlete: Record<string, unknown> = {
    [CUSTOM_DISCIPLINE_ATHLETE_ID_KEY]: "preview-athlete-id",
    [CUSTOM_DISCIPLINE_ATHLETE_ORDER_KEY]: 1,
  };
  const group: Record<string, unknown> = {
    id: "preview-group-id",
    name: "Groupe exemple",
    selected: true,
  };

  for (const entry of athleteSchema) {
    const path = entry.path?.trim();
    if (!path || path in athlete) {
      continue;
    }
    if (entry.valueType === "object") {
      athlete[path] = { name: "Exemple" };
      continue;
    }
    athlete[path] = mockScalarForValueType(entry.valueType);
  }

  for (const entry of groupSchema) {
    const path = entry.path?.trim();
    if (!path || path in group) {
      continue;
    }
    if (entry.valueType === "object") {
      group[path] = { name: "Exemple" };
      continue;
    }
    group[path] = mockScalarForValueType(entry.valueType);
  }

  const previewAthlete = athlete as CustomDisciplineAthleteRecord;
  const previewGroup = group as NonNullable<InjectedData["currentGroup"]>;
  const currentValue = options?.currentValue ?? "Valeur";

  return {
    athlete: previewAthlete,
    athletes: [previewAthlete] as unknown as InjectedData["athletes"],
    currentAthlete: previewAthlete as unknown as InjectedData["currentAthlete"],
    group: previewGroup,
    currentGroup: previewGroup,
    groups: [previewGroup],
    [CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE]: currentValue,
    [CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE]: "preview-row-id",
    [CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE]: "previewField",
  };
}

/**
 * Builds runtime injected data for a custom-discipline grid cell layout renderer.
 *
 * @param base - Shared meet/group/reference data for the custom group page.
 * @param input - Per-cell athlete context and display value.
 * @returns Data object consumed by {@link compileDocument} evaluation.
 */
export function buildCustomDisciplineCellRuntimeData(
  base: Partial<InjectedData>,
  input: BuildCustomDisciplineCellRuntimeDataInput,
): InjectedData {
  return {
    ...base,
    organisation: resolveCustomDisciplineOrganisation(base),
    athlete: input.athlete,
    athletes: input.athletes as unknown as InjectedData["athletes"],
    currentAthlete: input.athlete as unknown as InjectedData["currentAthlete"],
    currentValue: input.currentValue,
    selectedRowId: input.selectedRowId ?? "",
    selectedColumnKey: input.selectedColumnKey ?? "",
    __nodeInstanceKey: input.instanceKey,
    // External values are immutable during one render. Each compiler pass gets
    // a fresh weak cache, so in-place changes between renders remain observable.
    __cacheSourceSignatures: true,
  } as InjectedData;
}
